import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Collapse,
    Group,
    Modal,
    NumberInput,
    Stack,
    Text,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import { CaretDown, CaretRight, Warning } from '@phosphor-icons/react';

import { getMerchantId } from '@api/auth';
import { getOrgConfig, type MeasurementBasis } from '@api/organization';
import { getEntityMasterFilterOptions } from '@api/entity-attributes';
import { createProduct } from '@api/products';
import { readApiError } from '@utils/apiError';
import { toOptionalNumber } from '@utils/number';
import { COMMON_GST_RATES, validateGstRate, validateHsnCode } from '@utils/tax';

import ProductTypeSelect from './ProductTypeSelect';

interface AddProductModalProps {
    opened: boolean;
    onClose: () => void;
    /** Fired as soon as the product row exists, so the grid can reload. */
    onCreated: () => void;
    /** Opens the new product's own page, where makers, prices and the recipe are added. */
    onOpen?: (productId: string) => void;
}

const EAN_PATTERN = /^([0-9]{8}|[0-9]{12,14})$/;

/**
 * One product, the few fields a row needs to exist. Everything else - makers, prices, the
 * recipe - is added on the product's own page, which "Create and open" lands on.
 */
const AddProductModal: React.FC<AddProductModalProps> = ({ opened, onClose, onCreated, onOpen }) => {
    const [sku, setSku] = useState('');
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [productType, setProductType] = useState('');
    const [price, setPrice] = useState<string | number>('');
    const [more, setMore] = useState(false);
    const [subCategory, setSubCategory] = useState('');
    const [productGroup, setProductGroup] = useState('');
    const [ean, setEan] = useState('');
    const [weightGrams, setWeightGrams] = useState<string | number>('');
    const [volumeCbm, setVolumeCbm] = useState<string | number>('');
    const [unitsPerCase, setUnitsPerCase] = useState<string | number>('');
    const [gstRate, setGstRate] = useState<string>('');
    const [hsnCode, setHsnCode] = useState('');

    // The grid's own filter options double as the pick lists: category, sub-category, group.
    const [lists, setLists] = useState<Record<string, string[]>>({});
    const [basis, setBasis] = useState<MeasurementBasis>('WEIGHT');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setSku('');
        setName('');
        setCategory('');
        setProductType('');
        setPrice('');
        setSubCategory('');
        setProductGroup('');
        setEan('');
        setWeightGrams('');
        setVolumeCbm('');
        setUnitsPerCase('');
        setGstRate('');
        setHsnCode('');
        setFieldErrors({});
        setSaveError(null);
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        setMore(false);
        onClose();
    };

    useEffect(() => {
        if (!opened) return;
        void getEntityMasterFilterOptions('PRODUCT').then(setLists).catch(() => setLists({}));
        void getMerchantId()
            .then((merchantId) => (merchantId ? getOrgConfig(merchantId) : null))
            .then((org) => setBasis(org?.measurementBasis ?? 'WEIGHT'))
            .catch(() => setBasis('WEIGHT'));
    }, [opened]);

    const showWeight = basis === 'WEIGHT' || basis === 'BOTH';
    const showVolume = basis === 'VOLUME' || basis === 'BOTH';

    const validate = (): boolean => {
        const errors: Record<string, string> = {};
        if (!sku.trim()) errors.sku = 'SKU is required';
        if (!name.trim()) errors.name = 'Name is required';
        if (!category.trim()) errors.category = 'Category is required';
        if (ean.trim() && !EAN_PATTERN.test(ean.trim())) errors.ean = 'Must be an 8, 12, 13 or 14 digit barcode number';
        const gstError = validateGstRate(gstRate);
        if (gstError) errors.gstRate = gstError;
        const hsnError = validateHsnCode(hsnCode);
        if (hsnError) errors.hsnCode = hsnError;
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0 && !more && (errors.ean || errors.gstRate || errors.hsnCode)) setMore(true);
        return Object.keys(errors).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        setSaving(true);
        setSaveError(null);
        try {
            const created = await createProduct({
                sku: sku.trim(),
                name: name.trim(),
                category: category.trim(),
                subCategory: subCategory.trim() || undefined,
                productGroup: productGroup.trim() || undefined,
                productType: productType.trim() || undefined,
                price: toOptionalNumber(price),
                weightGrams: showWeight ? toOptionalNumber(weightGrams) : undefined,
                volumeCbm: showVolume ? toOptionalNumber(volumeCbm) : undefined,
                gstRate: toOptionalNumber(gstRate),
                hsnCode: hsnCode.trim() || undefined,
                unitsPerCase: toOptionalNumber(unitsPerCase),
                ean: ean.trim() || undefined,
            });
            onCreated();
            handleClose();
            onOpen?.(created.productId);
        } catch (err) {
            setSaveError(readApiError(err, 'Could not add the product. Please try again.'));
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={handleClose} title="Add product" size="md" centered>
            <Stack gap="md">
                {/* Paired boxes carry no description: a helper line under one label pushes
                    its box below the other's. Notes sit under the row instead. */}
                <Group grow align="flex-start">
                    <TextInput
                        label="SKU"
                        placeholder="e.g. PERI_120"
                        value={sku}
                        onChange={(e) => setSku(e.currentTarget.value)}
                        error={fieldErrors.sku}
                        withAsterisk
                        data-autofocus
                    />
                    <ProductTypeSelect value={productType} onChange={setProductType} />
                </Group>
                <Text size="xs" c="dimmed" mt={-8}>
                    The SKU is also the product ID and cannot be changed later.
                </Text>

                <TextInput
                    label="Name"
                    placeholder="e.g. Peri Peri Puff 120g"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    error={fieldErrors.name}
                    withAsterisk
                />

                <Group grow align="flex-start">
                    <Autocomplete
                        label="Category"
                        placeholder="Pick one, or type a new one"
                        data={lists.category ?? []}
                        value={category}
                        onChange={setCategory}
                        error={fieldErrors.category}
                        withAsterisk
                    />
                    <NumberInput label="MRP" placeholder="Optional" value={price} onChange={setPrice} min={0} decimalScale={2} />
                </Group>

                <UnstyledButton onClick={() => setMore((v) => !v)} fz="sm" c="blue">
                    <Group gap={4}>
                        {more ? <CaretDown size={12} /> : <CaretRight size={12} />}
                        More details
                        <Text span size="xs" c="dimmed">
                            (group, sub-category, EAN, {showWeight ? 'weight, ' : ''}{showVolume ? 'volume, ' : ''}units per case, GST, HSN)
                        </Text>
                    </Group>
                </UnstyledButton>

                <Collapse in={more}>
                    <Stack gap="sm">
                        <Group grow align="flex-start">
                            <Autocomplete label="Product group" placeholder="Optional" data={lists.productGroup ?? []} value={productGroup} onChange={setProductGroup} />
                            <Autocomplete label="Sub-category" placeholder="Optional" data={lists.subCategory ?? []} value={subCategory} onChange={setSubCategory} />
                        </Group>
                        <TextInput
                            label="EAN / barcode"
                            description="GTIN, 8 to 14 digits. Unique per brand."
                            placeholder="Optional"
                            value={ean}
                            onChange={(e) => setEan(e.currentTarget.value)}
                            error={fieldErrors.ean}
                        />
                        <Group grow align="flex-start">
                            {showWeight && (
                                <NumberInput label="Weight (g)" placeholder="Optional" value={weightGrams} onChange={setWeightGrams} min={0} />
                            )}
                            {showVolume && (
                                <NumberInput label="Volume (cbm)" placeholder="Optional" value={volumeCbm} onChange={setVolumeCbm} min={0} decimalScale={4} />
                            )}
                            <NumberInput
                                label="Units per case"
                                placeholder="Blank = not packed in cases"
                                value={unitsPerCase}
                                onChange={setUnitsPerCase}
                                min={1}
                                allowDecimal={false}
                            />
                        </Group>
                        <Group grow align="flex-start">
                            <Autocomplete
                                label="GST %"
                                placeholder="e.g. 18; blank is not 0%"
                                data={COMMON_GST_RATES}
                                value={gstRate}
                                onChange={setGstRate}
                                error={fieldErrors.gstRate}
                            />
                            <TextInput
                                label="HSN/SAC code"
                                placeholder="Optional"
                                value={hsnCode}
                                onChange={(e) => setHsnCode(e.currentTarget.value)}
                                error={fieldErrors.hsnCode}
                            />
                        </Group>
                    </Stack>
                </Collapse>

                <Text size="xs" c="dimmed">
                    Makers, prices and the recipe are added on the product&apos;s page afterwards.
                </Text>

                {saveError && (
                    <Alert color="red" icon={<Warning size={16} />}>
                        {saveError}
                    </Alert>
                )}

                <Group justify="flex-end" gap="sm" mt="xs">
                    <Button variant="subtle" color="gray" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button onClick={() => void submit()} loading={saving}>
                        {onOpen ? 'Create and open' : 'Create'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default AddProductModal;
